// ECR Tracker: the lifecycle vocabulary shared by the API and the page.
//
// Lives on the frontend side, like talentStages.js, so the Worker imports it
// from here and there is one list of lanes rather than two that drift apart.
// The twelve lanes follow the Rolls-Royce ECR journey as realigned on
// 02 October 2026: PM raises it, a designer prepares ECR1/2, AV reviews it,
// it is submitted, ECR3 authorises the work, the work is done, a designer
// prepares ECR4, AV reviews it, it is submitted, ECR5 closes it.
//
// Review and submission are separate lanes on purpose: the ECR team reviews
// several ECRs and then submits them together as a batch, so "reviewed, not
// yet submitted" is a real place for a card to sit. Neither review lane is
// gated; the board is the workflow, and a card moves when the work does.
import { makeCalendar } from '../../../src/lib/schedule.js';

export const LANE_GROUPS = ['pre_work', 'delivery', 'post_work'];

export const LANE_GROUP_LABELS = {
  pre_work: 'Pre-work',
  delivery: 'Delivery',
  post_work: 'Post-work',
};

// `flag` marks the two gating milestones the board exists to make visible:
// nothing can start before ECR3, and nothing can be billed before ECR5.
// `external` marks the lanes where the card is waiting on Rolls-Royce.
export const LANE_DEFS = [
  { key: 'ecr12_awaiting_assignment', group: 'pre_work',  label: 'ECR1/2 Awaiting Assignment',       description: 'Raised by the PM. Waiting for the ECR team to assign a designer and start it.' },
  { key: 'ecr12_in_progress',         group: 'pre_work',  label: 'ECR1/2 In Progress',               description: 'A designer is preparing the ECR1/2 submission.' },
  { key: 'ecr12_av_review',           group: 'pre_work',  label: 'ECR1/2 Awaiting AV Review',        description: 'The designer has finished the ECR1/2 and it is waiting for Alton Valley\'s internal review.' },
  { key: 'ecr12_ready_to_submit',     group: 'pre_work',  label: 'ECR1/2 Ready to Submit',           description: 'Reviewed internally and ready to be formally submitted.' },
  { key: 'ecr12_submitted',           group: 'pre_work',  label: 'ECR1/2 Submitted - Awaiting ECR3', description: 'Submitted to Rolls-Royce. Waiting for ECR3 approval.', external: true },
  { key: 'ecr3_received',             group: 'delivery',  label: 'ECR3 Received - Work Active',      description: 'ECR3 has been received and the work it covers is authorised and underway.', flag: 'Approved to work' },
  { key: 'work_complete',             group: 'delivery',  label: 'Work Complete - ECR4 Required',    description: 'The work is complete and an ECR4 is needed to document what was delivered. Assign or reassign the ECR4 designer here.' },
  { key: 'ecr4_in_progress',          group: 'post_work', label: 'ECR4 In Progress',                 description: 'A designer is preparing the ECR4 (As-Fitted) submission from the work actually completed.' },
  { key: 'ecr4_av_review',            group: 'post_work', label: 'ECR4 Awaiting AV Review',          description: 'The designer has finished the ECR4 and it is waiting for Alton Valley\'s internal review.' },
  { key: 'ecr4_ready_to_submit',      group: 'post_work', label: 'ECR4 Ready to Submit',             description: 'Reviewed internally and ready to be formally submitted.' },
  { key: 'ecr4_submitted',            group: 'post_work', label: 'ECR4 Submitted - Awaiting ECR5',   description: 'Submitted to Rolls-Royce. Waiting for ECR5 approval and sign-off.', external: true },
  { key: 'ecr5_received',             group: 'post_work', label: 'ECR5 Received',                    description: 'ECR5 has been received. The ECR lifecycle is complete and the work can be billed.', flag: 'Billable' },
];

export const LANES = LANE_DEFS.map(l => l.key);
export const LANE_LABELS = Object.fromEntries(LANE_DEFS.map(l => [l.key, l.label]));
export const LANE_BY_KEY = Object.fromEntries(LANE_DEFS.map(l => [l.key, l]));
export const FIRST_LANE = LANES[0];

// Lanes the board had before 02 October 2026. History rows keep the lane they
// were written with (migration 0369 moved the cards, not the history), so the
// page still needs a name for these. None of them is a lane a card can enter.
const RETIRED_LANE_LABELS = {
  ecr12_preparation:     'ECR1/2 Preparation',
  ecr12_internal_review: 'Internal Review',
  awaiting_ecr3:         'Awaiting ECR3',
  work_in_progress:      'Work In Progress',
  ecr4_preparation:      'ECR4 Preparation',
  ecr4_internal_review:  'ECR4 Internal Review',
  complete:              'Complete',
};

export const laneLabel = (key) => LANE_LABELS[key] || RETIRED_LANE_LABELS[key] || key;

// The ECR1/2 Submission Due Date (stored as target_date, its name before
// 02 October 2026) is the deadline for reaching ECR1/2 Submitted. It can make
// a card overdue only in the lanes before that, never at ECR3, ECR4 or ECR5.
export const DUE_DATE_LANES = LANES.slice(0, LANES.indexOf('ecr12_submitted'));

// The lanes each quick filter and summary tile resolve to.
export const LANE_SETS = {
  awaiting_ecr3:    ['ecr12_submitted'],
  approved_to_work: ['ecr3_received'],
  ecr4_required:    ['work_complete', 'ecr4_in_progress', 'ecr4_av_review', 'ecr4_ready_to_submit'],
  awaiting_ecr5:    ['ecr4_submitted'],
  billable:         ['ecr5_received'],
};

// The designer is a member of staff (designer_id), picked from everybody the
// way the PM is, so a new designer needs no deploy and can be told about the
// card. It was a fixed list of three names until 21 October 2026 (migration
// 0401). There is one designer field: whoever prepares ECR4 may not be who
// prepared ECR1/2, so it is reassigned on the card and each assignment is
// written to the history.

// ─── Updates for the people named on a card ──────────────────────────────────
// A change to a card by somebody else is an update for its PM and designer
// until they next open it. These read a history row (or a date correction,
// shaped as one) as a sentence, for the card face, the panel and the morning
// email alike, so all three say the same thing.
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const dayText = (ymd) => {
  const [y, m, d] = String(ymd).slice(0, 10).split('-');
  return MONTH_NAMES[m - 1] ? `${Number(d)} ${MONTH_NAMES[m - 1]} ${y}` : String(ymd);
};

const RETIRED_EVENT_TEXT = {
  pm_review: 'PM Review completed',
  infrastructure_review: 'Infrastructure Review completed',
  pm_review_override: 'PM Review approved by override',
  infrastructure_review_override: 'Infrastructure Review approved by override',
};

export function updateText(h) {
  switch (h.event) {
    case 'move': return h.from_lane ? `Moved from ${laneLabel(h.from_lane)} to ${laneLabel(h.to_lane)}` : `ECR raised in ${laneLabel(h.to_lane)}`;
    case 'designer': return h.note ? `Designer set to ${h.note}` : 'Designer removed';
    case 'pm': return h.note ? `PM set to ${h.note}` : 'PM removed';
    case 'blocked': return `Marked blocked: ${h.note}`;
    case 'unblocked': return 'No longer blocked';
    case 'next_action': return h.note ? `Next action: ${h.note}` : 'Next action cleared';
    case 'target_date': return h.note ? `ECR1/2 Submission Due Date set to ${dayText(h.note)}` : 'ECR1/2 Submission Due Date cleared';
    case 'notes': return 'Notes edited';
    case 'link': return h.note ? 'ECR documents link changed' : 'ECR documents link removed';
    case 'sow': return h.note ? `Linked to SOW ${h.note}` : 'SOW link removed';
    case 'project': return `Project changed to ${h.note}`;
    case 'archive': return h.note ? `Archived: ${h.note}` : 'Archived';
    case 'restore': return `Restored to ${laneLabel(h.to_lane)}`;
    default: return RETIRED_EVENT_TEXT[h.event] || h.note || 'Updated';
  }
}

// ─── Rolls-Royce response SLA ────────────────────────────────────────────────
// Rolls-Royce has 10 working days to respond to each ECR1/2 and ECR4
// submission. Entering the submitted lane starts the clock; a response stops
// it, whether that is ECR3/ECR5 (moving the card on to `responseLane` or
// beyond records it) or feedback recorded from the card. A breach is a
// warning on the card, never a reason to move it.

export const RR_SLA_WORKING_DAYS = 10;
export const SLA_APPROACHING_DAYS = 2;

export const SUBMISSIONS = {
  ecr12: { label: 'ECR1/2', lane: 'ecr12_submitted', responseLane: 'ecr3_received', responseType: 'ecr3_received' },
  ecr4:  { label: 'ECR4',   lane: 'ecr4_submitted',  responseLane: 'ecr5_received', responseType: 'ecr5_received' },
};

export const RESPONSE_LABELS = {
  ecr3_received: 'ECR3 Received',
  ecr5_received: 'ECR5 Received',
  feedback: 'Feedback / Query',
  amendment_required: 'Amendment Required',
  other: 'Other',
};
export const RESPONSE_TYPES = Object.keys(RESPONSE_LABELS);

// The response types that can answer a submission of this kind: ECR3 answers
// ECR1/2 and ECR5 answers ECR4, and the rest answer either.
export const responseTypesFor = (kind) =>
  RESPONSE_TYPES.filter(t => t !== SUBMISSIONS[kind === 'ecr12' ? 'ecr4' : 'ecr12'].responseType);

// ponytail: Monday to Friday only (V1). Pass UK bank holidays to makeCalendar
// if Rolls-Royce's SLA is ever agreed to exclude them.
const cal = makeCalendar();
const day = (d) => String(d).slice(0, 10);
// Working days after a, up to and including b, for a on or before b.
const workingDaysAfter = (a, b) => cal.workingDaysBetween(a, b) - (cal.isWorkingDay(a) ? 1 : 0);

/** The 10th working day after the submission date. */
export const slaDueDate = (submitted) => cal.addWorkingDays(cal.prevWorkingDay(day(submitted)), RR_SLA_WORKING_DAYS);

/** Where a running SLA stands on `today` (YYYY-MM-DD). */
export function slaStatus(dueDate, today) {
  if (today < dueDate) {
    const remaining = workingDaysAfter(today, dueDate);
    return { status: remaining <= SLA_APPROACHING_DAYS ? 'approaching' : 'within', remaining };
  }
  if (today === dueDate) return { status: 'due_today', remaining: 0 };
  return { status: 'breached', overdue: workingDaysAfter(dueDate, today) };
}

/** How a response scored: working days taken, working days late, and whether it was in time. */
export function slaResult(submitted, dueDate, responded) {
  const on = day(responded);
  return {
    // Never negative: a corrected response may be dated before its submission.
    taken: Math.max(0, workingDaysAfter(day(submitted), on)),
    overdue: on > dueDate ? workingDaysAfter(dueDate, on) : 0,
    within: on <= dueDate,
  };
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function slaText(s) {
  if (s.status === 'breached') return s.overdue ? `RR SLA: BREACHED · ${plural(s.overdue, 'working day')} overdue` : 'RR SLA: BREACHED';
  if (s.status === 'due_today') return 'RR SLA: Due today';
  return `RR SLA: ${plural(s.remaining, 'day')} remaining`;
}

export function slaResultText({ within, taken, overdue }) {
  if (within) return `Responded within SLA (${plural(taken, 'working day')})`;
  return overdue ? `Responded ${plural(overdue, 'working day')} outside SLA` : 'Responded outside SLA';
}

// ─── Lifecycle dates ─────────────────────────────────────────────────────────
// When each step of the lifecycle actually happened. Nothing here is a second
// set of dates: every milestone reads from where the tracker already records
// it, and a correction (ecr_date_corrections, insert-only) is the person
// telling BizOps the real date when that record is wrong, typically because a
// backlog ECR was entered and moved weeks after the event.
//
// Where a milestone's date comes from, in order:
//   - a submission milestone (ECR1/2 or ECR4 Submitted) is the latest SLA
//     submission of its kind, and ECR3 or ECR5 Received is the response to it
//     when Rolls-Royce answered with that. Corrections are written into the
//     submission row itself, so the due date and the result recalculate.
//   - anything else is the newer, by when BizOps was told, of the latest move
//     into that lane and the latest correction. A correction overrides the
//     move it corrects, and a later genuine move still records itself.
//   - ECR Raised is the card's creation, or its latest correction.
// The lane history is never rewritten: the move stays as the record of what
// happened in BizOps, the correction as the record of what happened in fact.

export const MILESTONES = [
  { key: 'raised',                lane: 'ecr12_awaiting_assignment', label: 'ECR Raised' },
  { key: 'ecr12_in_progress',     lane: 'ecr12_in_progress',         label: 'ECR1/2 Started' },
  { key: 'ecr12_av_review',       lane: 'ecr12_av_review',           label: 'ECR1/2 AV Review' },
  { key: 'ecr12_ready_to_submit', lane: 'ecr12_ready_to_submit',     label: 'ECR1/2 Ready to Submit' },
  { key: 'ecr12_submitted',       lane: 'ecr12_submitted',           label: 'ECR1/2 Submitted', submission: 'ecr12' },
  { key: 'ecr3_received',         lane: 'ecr3_received',             label: 'ECR3 Received',    response: 'ecr12' },
  { key: 'work_complete',         lane: 'work_complete',             label: 'Work Completed' },
  { key: 'ecr4_in_progress',      lane: 'ecr4_in_progress',          label: 'ECR4 Started' },
  { key: 'ecr4_av_review',        lane: 'ecr4_av_review',            label: 'ECR4 AV Review' },
  { key: 'ecr4_ready_to_submit',  lane: 'ecr4_ready_to_submit',      label: 'ECR4 Ready to Submit' },
  { key: 'ecr4_submitted',        lane: 'ecr4_submitted',            label: 'ECR4 Submitted',   submission: 'ecr4' },
  { key: 'ecr5_received',         lane: 'ecr5_received',             label: 'ECR5 Received',    response: 'ecr4' },
];
export const MILESTONE_BY_KEY = Object.fromEntries(MILESTONES.map(m => [m.key, m]));

// A move into a lane the board had before 02 October 2026 still dates the
// milestone that lane became.
const MILESTONE_OF_LANE = {
  ...Object.fromEntries(MILESTONES.filter(m => m.key !== 'raised').map(m => [m.lane, m.key])),
  ecr12_internal_review: 'ecr12_av_review',
  awaiting_ecr3:         'ecr12_submitted',
  work_in_progress:      'ecr3_received',
  ecr4_preparation:      'ecr4_in_progress',
  ecr4_internal_review:  'ecr4_av_review',
  complete:              'ecr5_received',
};

// A response that was not ECR3 or ECR5 (a query, an amendment) is corrected
// against its own submission under this name.
const RESPONSE_FIELD_LABELS = { ecr12_response: 'RR Response to ECR1/2', ecr4_response: 'RR Response to ECR4' };

/** The field a submission's response date is corrected under. */
export const responseField = (sub) =>
  (sub.response_type === SUBMISSIONS[sub.kind].responseType ? sub.response_type : `${sub.kind}_response`);

export const correctionFieldLabel = (field) =>
  MILESTONE_BY_KEY[field]?.label || RESPONSE_FIELD_LABELS[field] || field;

// Rows arrive newest first, so on a tie in the same second the first one wins.
const newest = (rows, at) => rows.reduce((best, r) => (!best || r[at] > best[at] ? r : best), null);

/**
 * Every milestone with its effective date. `submissions` and `corrections`
 * newest first, as the card detail returns them; `history` in any order.
 * Each row: key, label, lane, value (a stamp, a YYYY-MM-DD date or null),
 * submission_id where the date lives on an SLA submission, corrected when it
 * has ever been corrected, and reached when the card has got that far.
 */
export function lifecycleDates(card, history = [], submissions = [], corrections = []) {
  const at = LANES.indexOf(card.lane);
  return MILESTONES.map((m) => {
    const fix = (sid) => corrections.filter(c => c.field === m.key && (c.submission_id || null) === sid);
    let sub = null;
    if (m.submission) sub = submissions.find(s => s.kind === m.submission) || null;
    if (m.response) sub = submissions.find(s => s.kind === m.response && s.response_type === m.key && s.responded_at) || null;
    const row = { key: m.key, label: m.label, lane: m.lane, reached: LANES.indexOf(m.lane) <= at };
    if (sub) {
      return { ...row, value: m.submission ? sub.submitted_at : sub.responded_at, submission_id: sub.id, corrected: fix(sub.id).length > 0 };
    }
    const fixes = fix(null);
    const correction = newest(fixes, 'corrected_at');
    const auto = m.key === 'raised'
      ? { changed_at: card.created_at }
      : newest(history.filter(h => (h.event || 'move') === 'move' && h.from_lane && MILESTONE_OF_LANE[h.to_lane] === m.key), 'changed_at');
    const value = correction && (!auto || correction.corrected_at >= auto.changed_at) ? correction.new_value : auto?.changed_at ?? null;
    return { ...row, value, submission_id: null, corrected: fixes.length > 0 };
  });
}

// The step each date should not normally come before: a response after the
// submission it answers, a submission after its preparation started, and the
// work after ECR3 authorised it. Only these pairs are checked. A backlog card
// carries the day it was entered as every date nobody has corrected yet, so
// comparing against every neighbour would ask for Save Anyway on nearly every
// correction and teach people to click through the warning.
export const NOT_BEFORE = {
  ecr12_submitted: 'ecr12_in_progress',
  ecr3_received:   'ecr12_submitted',
  work_complete:   'ecr3_received',
  ecr4_submitted:  'ecr4_in_progress',
  ecr5_received:   'ecr4_submitted',
};

/**
 * Warnings, never refusals: historical data is allowed to be odd, but nobody
 * should save an odd date without being told. `rows` are lifecycle rows with
 * the proposed dates in place; `changed` the keys that changed. Dates nobody
 * changed are not nagged about.
 */
export function dateWarnings(rows, changed, today) {
  const out = [];
  const byKey = Object.fromEntries(rows.map(r => [r.key, r]));
  for (const r of rows) {
    if (r.value && changed.has(r.key) && day(r.value) > today) {
      out.push(`The ${r.label} date entered is in the future. Please confirm this is correct.`);
    }
  }
  for (const [laterKey, earlierKey] of Object.entries(NOT_BEFORE)) {
    const later = byKey[laterKey];
    const earlier = byKey[earlierKey];
    if (!later?.value || !earlier?.value || !(changed.has(laterKey) || changed.has(earlierKey))) continue;
    if (day(later.value) >= day(earlier.value)) continue;
    out.push(changed.has(laterKey)
      ? `The ${later.label} date entered is earlier than the ${earlier.label} date. Please confirm these dates are correct.`
      : `The ${earlier.label} date entered is later than the ${later.label} date. Please confirm these dates are correct.`);
  }
  return out;
}

/**
 * The warnings for a set of proposed corrections ({ field, submission_id?,
 * date }). A change to the submission a milestone reads from is a change to
 * that milestone; a change to an earlier submission cycle is checked against
 * its own submitted and response dates. Shared so the dialog warns about
 * exactly what the server will ask to be confirmed.
 */
export function correctionWarnings(lifecycle, submissions, changes, today) {
  const isMilestone = (c) => lifecycle.some(r => r.key === c.field && (r.submission_id || null) === (c.submission_id || null));
  const rows = lifecycle.map(r => {
    const c = changes.find(x => x.field === r.key && isMilestone(x));
    return c ? { ...r, value: c.date } : r;
  });
  const out = dateWarnings(rows, new Set(changes.filter(isMilestone).map(c => c.field)), today);
  for (const c of changes.filter(x => x.submission_id && !isMilestone(x))) {
    const s = submissions.find(x => x.id === c.submission_id);
    if (!s) continue;
    const label = SUBMISSIONS[s.kind].label;
    const isSubmitted = c.field === `${s.kind}_submitted`;
    const submittedKey = `${s.kind}_submitted`;
    const responseKey = SUBMISSIONS[s.kind].responseType;
    const pair = [
      { key: submittedKey, label: `${label} Submitted`, value: isSubmitted ? c.date : s.submitted_at },
      { key: responseKey, label: correctionFieldLabel(responseField(s)), value: isSubmitted ? s.responded_at : c.date },
    ];
    out.push(...dateWarnings(pair, new Set([isSubmitted ? submittedKey : responseKey]), today));
  }
  return [...new Set(out)];
}
